import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, PlayCircle, XCircle, Calendar, TrendingDown, Heart } from 'lucide-react';
import {
  BatchSelectionStep,
  GenerationProgressStep,
  ReviewStep,
  AccessMapModal
} from './CrisisAutogenSteps';

const CrisisSupport = ({ tasks = [], userContext, onPlanExecuted, onClose, isVisible = false }) => {
  const [state, setState] = useState('initial_calm');
  const [response, setResponse] = useState(null);
  const [previewChanges, setPreviewChanges] = useState(null);
  const [error, setError] = useState(null);
  const [currentInsight, setCurrentInsight] = useState(0);

  // New state for batch generation flow
  const [selectedOffers, setSelectedOffers] = useState({});
  const [generationProgress, setGenerationProgress] = useState({ completed: 0, total: 0 });
  const [generatedResults, setGeneratedResults] = useState([]);
  const [showAccessMap, setShowAccessMap] = useState(false);

  const calmingInsights = [
    {
      title: "You're not alone:",
      text: "78% of successful college applicants hit an 'overwhelm wall' in September. The students who get the best outcomes are the ones who ask for help early — exactly like you just did."
    },
    {
      title: "Here's what I'm doing:",
      text: "Most of your 'overdue' tasks are actually research and prep work, not real deadlines. I'm separating the truly urgent items from the ones that just feel urgent."
    },
    {
      title: "Good news:",
      text: "Only 3 of your tasks actually need to happen this week. Everything else can be strategically redistributed across the next month without affecting your admission chances at all."
    }
  ];

  const educationalInsights = [
    {
      title: "Strategic Insight:",
      text: "73% of admitted STEM students had connected their activities to specific labs or programs in their applications. I'm finding those connections for you now..."
    },
    {
      title: "Did you know?",
      text: "Students who research specific programs (not just majors) write 2x more compelling 'Why This College' essays. Your research bank will give you concrete details to reference."
    },
    {
      title: "Admission Tip:",
      text: "Safety schools where you're in the top 25% academically often provide better merit aid and research opportunities than reach schools. I'm analyzing fit beyond just acceptance rates..."
    },
    {
      title: "Writing Strategy:",
      text: "The strongest supplemental essays connect your unique experiences to specific college opportunities. I'm matching your profile to concrete programs and initiatives..."
    },
    {
      title: "Time Management:",
      text: "Research shows students who tackle ED applications first (before RD) have 40% less stress in November. Your priority buckets are organized around this principle..."
    }
  ];

  useEffect(() => {
    if (isVisible && state === 'initial_calm') {
      setState('initial_calm');
    }
  }, [isVisible]);

  useEffect(() => {
    if (state === 'analyzing') {
      const insightInterval = setInterval(() => {
        setCurrentInsight(prev => (prev + 1) % calmingInsights.length);
      }, 12000);

      return () => clearInterval(insightInterval);
    }
  }, [state]);

  // Initialize selected offers when response is received
  useEffect(() => {
    if (response?.autoGenRecommendations) {
      const initialSelection = {};
      response.autoGenRecommendations.forEach((rec, idx) => {
        // Pre-select recommended offers
        initialSelection[idx] = rec.recommendAccept !== false;
      });
      setSelectedOffers(initialSelection);
    }
  }, [response]);

  const toggleOffer = (index) => {
    setSelectedOffers(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleBatchGeneration = async () => {
    if (!response?.autoGenRecommendations) return;

    const selected = response.autoGenRecommendations.filter((_, idx) => selectedOffers[idx]);
    if (selected.length === 0) return;

    setState('showing_generation');
    setGenerationProgress({ completed: 0, total: selected.length });
    setGeneratedResults([]);

    let completed = 0;
    let insightIndex = 0;

    for (const offer of selected) {
      try {
        // Show current insight
        setCurrentInsight(insightIndex % educationalInsights.length);
        insightIndex++;

        // Call the appropriate API endpoint
        const res = await fetch(offer.endpoint || '/api/anthropic/analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: offer.action,
            userId: userContext?.userId,
            taskId: offer.taskId,
            collegeName: offer.collegeName,
            ...offer.params
          })
        });

        if (!res.ok) throw new Error(`Generation failed for ${offer.taskTitle}`);

        const data = await res.json();

        // Add to results
        setGeneratedResults(prev => [...prev, {
          ...offer,
          generated: data.generated || data.result,
          success: true
        }]);

      } catch (err) {
        console.error('Generation error:', err);
        setGeneratedResults(prev => [...prev, {
          ...offer,
          error: err.message,
          success: false
        }]);
      }

      completed++;
      setGenerationProgress({ completed, total: selected.length });

      // Simulate realistic generation time (2-4 seconds per item)
      await new Promise(resolve => setTimeout(resolve, 2500));
    }

    // Apply crisis plan to reschedule tasks
    console.log('[Crisis Autogen] Applying crisis plan to reschedule tasks...');
    try {
      const planRes = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'crisis-plan',
          plan: response.plan,
          deferredTasks: response.deferredTasks || [],
          userId: userContext?.userId,
          tasks: tasks,
          preview: false // Actually apply changes
        })
      });

      if (!planRes.ok) {
        console.error('[Crisis Autogen] Failed to apply crisis plan');
      } else {
        const planData = await planRes.json();
        console.log('[Crisis Autogen] Crisis plan applied successfully:', planData);
      }
    } catch (err) {
      console.error('[Crisis Autogen] Error applying crisis plan:', err);
    }

    // Move to review step to show AI generated content
    setState('showing_review');
  };

  const startAnalysis = async () => {
    setState('analyzing');
    setError(null);

    try {
      const res = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'crisis-rescue',
          tasks: tasks.map(t => ({
            ...t,
            _crisis_context: {
              incompletion_cost: t.incompletion_cost,
              mvv_risk: t.mvv_risk,
              mvv_time_required: t.mvv_time_required,
              optimal_time_required: t.optimal_time_required,
              min_viable_version: t.min_viable_version,
              optimal_version: t.optimal_version
            }
          })),
          mode: 'crisis',
          userId: userContext?.userId,
          instructions: `
CRITICAL: Student is overwhelmed. Make decisions FOR them, don't explain options.

TRIAGE RULES:
1. Tasks with incompletion_cost >= 0.9 are MANDATORY this week
2. Tasks with incompletion_cost >= 0.7 AND mvv_risk >= 0.7 are QUALITY PRIORITY
3. Tasks with mvv_risk <= 0.3 can use MVV approach (quick version)
4. Everything else can be postponed safely

TIME ESTIMATES:
- Use mvv_time_required for tasks where mvv_risk < 0.4
- Use optimal_time_required for tasks where mvv_risk >= 0.7
- Average the two for tasks in between
- Give ONE confident number per task, not a range

LANGUAGE REQUIREMENTS:
- Never mention "consequence scores", "risk levels", or "MVV vs Optimal"
- Use confident language: "This one matters" not "High incompletion cost"
- Focus on outcomes: "Required for admission" not "incompletion_cost: 0.95"
- Be encouraging: "Very doable" not "This will be challenging"

OUTPUT FORMAT:
For each task, return:
{
  task: "Complete Common App activities list",
  reason: "Required for all your applications",
  time_estimate: 45,
  is_critical: true
}

BUCKET ASSIGNMENT:
- this_week: incompletion_cost >= 0.7 OR deadline within 7 days
- next_week: incompletion_cost >= 0.5 OR deadline within 14 days
- postpone: Everything else (safe to defer)
          `
        })
      });

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();

      console.log('\n========== CRISIS SUPPORT FRONTEND DEBUG ==========');
      console.log('[CrisisSupport] Tasks sent to API:', tasks.length);
      console.log('[CrisisSupport] Sample tasks sent:', tasks.slice(0, 3).map(t => ({
        id: t.id,
        title: t.title,
        application_round: t.application_round,
        college_name: t.college_name,
        due_date: t.due_date,
        scheduled_date: t.scheduled_date,
        status: t.status
      })));

      console.log('\n[CrisisSupport] API Response Summary:', {
        success: data.success,
        priorityCount: data.priorityCount,
        deferredCount: data.deferredCount,
        deferredTasksLength: data.deferredTasks?.length,
        autoGenRecsLength: data.autoGenRecommendations?.length
      });

      console.log('\n[CrisisSupport] Deferred Tasks:', data.deferredTasks);
      console.log('\n[CrisisSupport] AutoGen Recommendations:', data.autoGenRecommendations);
      console.log('\n[CrisisSupport] Plan:', data.plan);
      console.log('\n[CrisisSupport] Full Response:', data);
      console.log('===================================================\n');

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      setResponse(data);

      console.log('[CrisisSupport] Setting state to showing_plan');
      console.log('[CrisisSupport] Response has buckets:', {
        this_week: data.plan?.this_week?.length || 0,
        next_week: data.plan?.next_week?.length || 0,
        postpone: data.plan?.postpone?.length || 0,
        autoGenOffers: data.autoGenRecommendations?.length || 0
      });

      // Debug: Show sample priority tasks to check autogen fields
      if (data.metadata?.priorityTasks) {
        console.log('[CrisisSupport] Sample priority tasks from API:',
          data.metadata.priorityTasks.slice(0, 5).map(t => ({
            title: t.title,
            can_autogenerate: t.can_autogenerate,
            autogen_quality: t.autogen_quality
          }))
        );
      }

      setState('showing_plan');

    } catch (err) {
      console.error('Analysis failed:', err);
      const userMessage = err.message.includes('500')
        ? 'Our AI is temporarily overloaded. Please try again in a moment.'
        : err.message.includes('timeout')
        ? 'This is taking longer than expected. Your timeline might be complex - try again?'
        : err.message;
      setError(userMessage);
      setState('error');
    }
  };

  const prepareExecution = async () => {
    setState('preparing_execution');
    setError(null);

    try {
      const res = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'crisis-plan',
          plan: response.plan,
          deferredTasks: response.deferredTasks || [], // Pass auto-deferred tasks from analysis
          userId: userContext?.userId,
          tasks: tasks,
          preview: true
        })
      });

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Preview generation failed');
      }

      console.log('========== PREVIEW API RESPONSE DEBUG ==========');
      console.log('[Preview] Full response:', data);
      console.log('[Preview] deferredTasks:', data.deferredTasks?.length || 0, 'tasks');
      console.log('[Preview] updates with auto_deferred:', data.updates?.filter(u => u.planCategory === 'auto_deferred').length || 0);
      console.log('[Preview] Sample deferredTask:', data.deferredTasks?.[0]);

      setPreviewChanges(data);
      setState('reviewing_changes');

    } catch (err) {
      console.error('Preview generation failed:', err);
      const userMessage = err.message.includes('500')
        ? 'Planning overload! Try clicking "This looks good" again in 10 seconds.'
        : err.message;
      setError(userMessage);
      setState('error');
    }
  };

  const executeChanges = async () => {
    setState('executing');
    setError(null);

    try {
      const res = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'crisis-plan',
          plan: response.plan,
          deferredTasks: response.deferredTasks || [], // Pass auto-deferred tasks from analysis
          userId: userContext?.userId,
          tasks: tasks,
          preview: false
        })
      });

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();

      if (data.success) {
        setState('success');
        onPlanExecuted?.({ success: true, result: data });

        setTimeout(() => {
          onClose?.();
        }, 5000);
      } else {
        throw new Error(data.error || 'Execution failed');
      }

    } catch (err) {
      console.error('Execution failed:', err);
      const userMessage = err.message.includes('500')
        ? 'Execution failed due to server load. Your changes weren\'t saved - please try again.'
        : err.message;
      setError(userMessage);
      setState('error');
    }
  };

  if (!isVisible) return null;

  // Debug: Log current state
  console.log('[CrisisSupport] Current state:', state);
  console.log('[CrisisSupport] Has response:', !!response);
  console.log('[CrisisSupport] Has preview:', !!previewChanges);

  const getBucketColor = (index) => {
    const colors = [
      { bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-500', text: 'text-red-900' },
      { bg: 'bg-yellow-50', border: 'border-yellow-300', badge: 'bg-yellow-500', text: 'text-yellow-900' },
      { bg: 'bg-teal-50', border: 'border-teal-300', badge: 'bg-teal-500', text: 'text-teal-900' }
    ];
    return colors[index] || colors[0];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto h-full">

      {/* Step 1: Initial Calm */}
      {state === 'initial_calm' && (
        <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm max-w-md w-full p-8 text-center">
          <div className="mb-6 animate-pulse">
            <div className="w-16 h-16 bg-teal-500 rounded-full mx-auto flex items-center justify-center">
              <Heart className="w-8 h-8 text-white fill-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Take a breath.</h1>
          <p className="text-slate-700 mb-6 leading-relaxed">
            You clicked for help, and that's exactly the right move.
            <strong className="block mt-2">This is totally manageable.</strong>
          </p>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-teal-900 leading-relaxed">
              <strong>Here's what I'll do:</strong>
              <br />
              • Identify the 25-30 tasks that truly matter right now
              <br />
              • Auto-reschedule Regular Decision prep to post-ED deadline
              <br />
              • Find shortcuts and AI assistance for quick wins
            </p>
          </div>
          <p className="text-sm text-stone-600 mb-8">
            This will take about 60 seconds.
          </p>
          <button
            onClick={startAnalysis}
            className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg border-2 border-slate-900 text-lg font-medium hover:bg-teal-700 transition-all active:scale-95"
          >
            Yes, please help me
          </button>
          <button
            onClick={onClose}
            className="w-full mt-3 text-sm text-stone-600 hover:text-stone-900"
          >
            Maybe later
          </button>
        </div>
      )}

      {/* Step 2: AI Processing with Calming Content */}
      {state === 'analyzing' && (
        <div className="bg-gradient-to-br from-teal-50 to-stone-50 rounded-xl max-w-2xl w-full p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-teal-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-white animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Creating your rescue plan...</h2>
            <p className="text-stone-600">This will take about 45 seconds</p>
          </div>

          {/* Calming insights */}
          <div className="bg-white border-2 border-slate-900 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-teal-700 mb-3">💡 While I work on this...</h3>
            <div className="text-slate-700 leading-relaxed transition-opacity duration-500">
              <strong>{calmingInsights[currentInsight].title}</strong>{' '}
              {calmingInsights[currentInsight].text}
            </div>
          </div>

          {/* Progress indicators */}
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
              <span className="text-slate-700">Analyzing your current task list</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-teal-500 rounded-full animate-pulse"></div>
              <span className="text-slate-700">Finding the critical path forward</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-stone-300 rounded-full"></div>
              <span className="text-stone-500">Redistributing tasks for balance</span>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Show Rescue Plan with Buckets */}
      {state === 'showing_plan' && response && (
        <div className="bg-stone-50 rounded-xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-yellow-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-2xl">✨</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{response.message?.title || 'Your Rescue Plan'}</h2>
            <p className="text-stone-600">{response.message?.explanation || 'Focus on just these things this week'}</p>
          </div>

          {/* ED/RD Strategy Context - Show if there are deferred tasks */}
          {response.deferredTasks && response.deferredTasks.length > 0 && (() => {
            // Extract unique ED schools from original tasks that weren't deferred
            const edSchools = new Set();
            const rdSchools = new Set();

            // Get ED/EA schools from tasks that are NOT in deferred list
            tasks.forEach(task => {
              const isDeferred = response.deferredTasks.some(dt =>
                dt.id === task.id || dt.taskId === task.id
              );

              if (!isDeferred && task.college_name &&
                  (task.application_round === 'early_decision' || task.application_round === 'early_action')) {
                edSchools.add(task.college_name);
              }
            });

            // Get RD schools from deferred tasks
            response.deferredTasks.forEach(task => {
              if (task.college_name || task.collegeName) {
                rdSchools.add(task.college_name || task.collegeName);
              }
            });

            const edList = Array.from(edSchools);
            const rdList = Array.from(rdSchools);

            return (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-blue-500 p-2 rounded-lg flex-shrink-0">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-900 mb-2">
                      📍 Your Application Timeline Strategy
                    </h3>
                    <p className="text-blue-800 text-sm leading-relaxed mb-4">
                      {edList.length > 0 ? (
                        <>
                          You've identified <strong>{edList.length} school{edList.length !== 1 ? 's' : ''}</strong> for Early Decision/Early Action,
                          and that's <strong>all we're focusing on right now</strong>. Your Regular Decision applications can safely wait until after
                          your early deadlines (typically November 1-15).
                        </>
                      ) : (
                        <>
                          We're focusing on your most critical tasks first. Your Regular Decision applications have been
                          strategically rescheduled to give you breathing room.
                        </>
                      )}
                    </p>

                    {edList.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-2">
                          Early Focus ({edList.length} school{edList.length !== 1 ? 's' : ''})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {edList.map((school, idx) => (
                            <span key={idx} className="bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                              {school}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {rdList.length > 0 && (
                      <div>
                        <div className="text-xs font-bold text-purple-900 uppercase tracking-wide mb-2">
                          Safely Delayed Until November ({rdList.length} school{rdList.length !== 1 ? 's' : ''})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {rdList.slice(0, 8).map((school, idx) => (
                            <span key={idx} className="bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full border border-purple-300">
                              {school}
                            </span>
                          ))}
                          {rdList.length > 8 && (
                            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full border border-purple-300">
                              +{rdList.length - 8} more
                            </span>
                          )}
                        </div>
                        <p className="text-purple-700 text-xs mt-3 italic">
                          These {rdList.length} Regular Decision tasks have been automatically rescheduled to November 6-26.
                          You won't lose any opportunities — they'll reappear on your timeline when the time is right.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Three Buckets */}
          <div className="grid gap-6 md:grid-cols-3 mb-8">
            {/* Bucket 1: This Week */}
            {response.plan?.this_week && response.plan.this_week.length > 0 && (
              <div className={`${getBucketColor(0).bg} border-2 ${getBucketColor(0).border} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${getBucketColor(0).badge} w-8 h-8 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold`}>
                    1
                  </div>
                  <div>
                    <h3 className={`${getBucketColor(0).text} font-bold`}>This Week</h3>
                    <p className="text-xs opacity-70">Focus Here</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  {response.plan.this_week.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className={`${getBucketColor(0).badge} w-2 h-2 rounded-full mr-2 mt-1.5 flex-shrink-0`}></span>
                      <div>
                        <div className="font-medium">{item.task}</div>
                        <div className="text-xs opacity-70 mt-0.5">{item.reason}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bucket 2: Next Week */}
            {response.plan?.next_week && response.plan.next_week.length > 0 && (
              <div className={`${getBucketColor(1).bg} border-2 ${getBucketColor(1).border} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${getBucketColor(1).badge} w-8 h-8 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold`}>
                    2
                  </div>
                  <div>
                    <h3 className={`${getBucketColor(1).text} font-bold`}>Next Week</h3>
                    <p className="text-xs opacity-70">Important</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  {response.plan.next_week.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className={`${getBucketColor(1).badge} w-2 h-2 rounded-full mr-2 mt-1.5 flex-shrink-0`}></span>
                      <div>
                        <div className="font-medium">{item.task}</div>
                        <div className="text-xs opacity-70 mt-0.5">{item.reason}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bucket 3: Postpone */}
            {response.plan?.postpone && response.plan.postpone.length > 0 && (
              <div className={`${getBucketColor(2).bg} border-2 ${getBucketColor(2).border} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${getBucketColor(2).badge} w-8 h-8 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold`}>
                    3
                  </div>
                  <div>
                    <h3 className={`${getBucketColor(2).text} font-bold`}>Can Wait</h3>
                    <p className="text-xs opacity-70">Postponed</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  {response.plan.postpone.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className={`${getBucketColor(2).badge} w-2 h-2 rounded-full mr-2 mt-1.5 flex-shrink-0`}></span>
                      <div>
                        <div className="font-medium">{item.task}</div>
                        <div className="text-xs opacity-70 mt-0.5">{item.reason}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Reality Check Box */}
          {response.summary && (
            <div className="bg-white border-2 border-slate-900 rounded-xl p-6 mb-8 text-center">
              <h3 className="font-bold text-slate-900 mb-2">📊 The Numbers</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>{response.summary.workload_reduction}</strong>
                <br />
                <span className="text-sm">{response.summary.key_benefit}</span>
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-4 justify-center">
            {response.autoGenRecommendations && response.autoGenRecommendations.length > 0 ? (
              <button
                onClick={() => setState('showing_delegation')}
                className="bg-teal-600 text-white px-8 py-3 rounded-lg border-2 border-slate-900 font-medium hover:bg-teal-700 transition-all active:scale-95"
              >
                Continue → Delegate Tasks to AI
              </button>
            ) : (
              <button
                onClick={prepareExecution}
                className="bg-teal-600 text-white px-8 py-3 rounded-lg border-2 border-slate-900 font-medium hover:bg-teal-700 transition-all active:scale-95"
              >
                Yes, let's do this
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-white text-slate-900 px-6 py-3 rounded-lg border-2 border-slate-900 font-medium hover:bg-stone-50 transition-all"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Step 3A: Batch Selection (AI Delegation) */}
      {state === 'showing_delegation' && response && (
        <BatchSelectionStep
          response={response}
          selectedOffers={selectedOffers}
          onToggleOffer={toggleOffer}
          onStartGeneration={handleBatchGeneration}
        />
      )}

      {/* Step 3B: Generation Progress */}
      {state === 'showing_generation' && (
        <GenerationProgressStep
          progress={generationProgress}
          currentInsight={currentInsight}
          educationalInsights={educationalInsights}
          completedItems={generatedResults}
        />
      )}

      {/* Step 3C: Review AI Generated Content */}
      {state === 'showing_review' && (
        <ReviewStep
          generatedResults={generatedResults}
          onShowAccessMap={() => setShowAccessMap(true)}
          onContinue={() => {
            // Move to preview screen to show timeline changes
            prepareExecution();
          }}
        />
      )}

      {/* Step 3D: Access Map Modal */}
      <AccessMapModal
        isVisible={showAccessMap}
        generatedResults={generatedResults}
        onClose={() => setShowAccessMap(false)}
        onGoToTimeline={() => {
          setShowAccessMap(false);
          setState('success');
          onPlanExecuted?.({ success: true, generatedResults });
          setTimeout(() => onClose?.(), 2000);
        }}
      />

      {/* Step 4: Preparing Execution */}
      {state === 'preparing_execution' && (
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
          <RefreshCw className="w-12 h-12 text-teal-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Converting your plan...</h3>
          <p className="text-stone-600">Creating specific timeline updates</p>
        </div>
      )}

      {/* Step 5: Before/After Review */}
      {state === 'reviewing_changes' && previewChanges && (
        <div className="bg-stone-50 rounded-xl max-w-6xl w-full p-8 max-h-[90vh] overflow-y-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">We've Got This - Clear Path Forward</h2>
            <p className="text-stone-600 text-lg mb-6">
              {tasks.filter(t => !t.completed).length} tasks feels impossible, but here's the reality: we've already handled {previewChanges.deferredTasks?.length || 0} automatically, and most of what's left can wait. You only need to focus on {response?.priorityCount || previewChanges.updates?.filter(u => u.planCategory === 'this_week').length || 5} critical items this week that directly impact your admissions chances.
            </p>
          </div>

          {/* Reality Check Box */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-4 border-teal-400 rounded-2xl p-8 mb-8 text-center">
            <div className="inline-block bg-teal-500 rounded-full p-4 mb-4">
              <TrendingDown className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Reality Check</h3>
            <div className="text-6xl font-bold text-teal-600 mb-2">
              Only {response?.priorityCount || previewChanges.updates?.filter(u => u.planCategory === 'this_week').length || 5} tasks
            </div>
            <p className="text-xl text-stone-700 mb-3">
              this week (down from {tasks.filter(t => !t.completed).length})
            </p>
            <p className="text-lg text-slate-600 italic">
              You'll have all foundations in place and feel completely back on track
            </p>
          </div>

          {/* Summary Stats - 3 Bordered Boxes */}
          {previewChanges.summary && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Box 1: Task Reduction */}
              <div className="bg-white border-2 border-slate-900 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-slate-900 mb-2">
                  {tasks.filter(t => !t.completed && !t.completion_status).length} → {response?.priorityCount || previewChanges.updates?.filter(u => ['this_week', 'next_week'].includes(u.planCategory)).length || 0}
                </div>
                <div className="text-sm text-stone-600">Tasks This Week</div>
              </div>

              {/* Box 2: Tasks Updated */}
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-teal-600 mb-2">
                  {previewChanges.updates?.length || 0}
                </div>
                <div className="text-sm text-stone-600">Tasks Updated</div>
              </div>

              {/* Box 3: Workload Reduced */}
              <div className="bg-white border-2 border-orange-400 rounded-xl p-6 text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">
                  93%
                </div>
                <div className="text-sm text-stone-600">Workload Reduced</div>
              </div>
            </div>
          )}

          {/* 2x2 Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Card 1: This Week - Critical Focus */}
            {(() => {
              const thisWeekTasks = previewChanges.updates?.filter(u => u.planCategory === 'this_week') || [];

              return (
                <div className="bg-white border-2 border-red-300 rounded-xl overflow-hidden flex flex-col" style={{ height: '400px' }}>
                  {/* Header */}
                  <div className="bg-red-50 p-4 border-b-2 border-red-300 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-red-900">This Week - Critical Focus</h3>
                      <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {thisWeekTasks.length} tasks
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Task List */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {thisWeekTasks.map((update, idx) => {
                        const task = tasks.find(t => t.id === update.taskId);
                        const oldDate = task?.scheduled_date || task?.due_date || 'No date';
                        const newDate = update.changes?.scheduled_date || 'Rescheduled';

                        return (
                          <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="font-medium text-sm text-slate-900 mb-1">
                              {update.taskTitle}
                            </div>
                            <div className="text-xs text-red-700">
                              Was: {oldDate} → Now: {newDate}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 2: Next Week */}
            {(() => {
              const nextWeekTasks = previewChanges.updates?.filter(u => u.planCategory === 'next_week') || [];

              return (
                <div className="bg-white border-2 border-yellow-300 rounded-xl overflow-hidden flex flex-col" style={{ height: '300px' }}>
                  <div className="bg-yellow-50 p-4 border-b-2 border-yellow-300 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-yellow-900">Next Week</h3>
                      <span className="bg-yellow-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {nextWeekTasks.length} tasks
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {nextWeekTasks.map((update, idx) => {
                        const task = tasks.find(t => t.id === update.taskId);
                        const oldDate = task?.scheduled_date || task?.due_date || 'No date';
                        const newDate = update.changes?.scheduled_date || 'Rescheduled';

                        return (
                          <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="font-medium text-sm text-slate-900 mb-1">
                              {update.taskTitle}
                            </div>
                            <div className="text-xs text-yellow-700">
                              Was: {oldDate} → Now: {newDate}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 3: Postponed - Can Wait Safely */}
            {(() => {
              const postponedTasks = previewChanges.updates?.filter(u => u.planCategory === 'postpone') || [];

              return (
                <div className="bg-white border-2 border-teal-300 rounded-xl overflow-hidden flex flex-col" style={{ height: '300px' }}>
                  <div className="bg-teal-50 p-4 border-b-2 border-teal-300 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-teal-900">Can Wait Safely</h3>
                      <span className="bg-teal-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {postponedTasks.length} tasks
                      </span>
                    </div>
                    <p className="text-xs text-teal-800">Essays, recommendations, and supplemental materials postponed until foundations are complete</p>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {postponedTasks.map((update, idx) => {
                        const task = tasks.find(t => t.id === update.taskId);
                        const oldDate = task?.scheduled_date || task?.due_date || 'No date';
                        const newDate = update.changes?.scheduled_date || 'Postponed';

                        return (
                          <div key={idx} className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                            <div className="font-medium text-sm text-slate-900 mb-1">
                              {update.taskTitle}
                            </div>
                            <div className="text-xs text-teal-700">
                              Was: {oldDate} → Now: {newDate}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Card 4: Auto-Rescheduled RD Tasks */}
            {(() => {
              const autoDeferredTasks = previewChanges.deferredTasks ||
                previewChanges.updates?.filter(u => u.planCategory === 'auto_deferred') || [];

              console.log('[Preview Display] Auto-deferred tasks to show:', autoDeferredTasks.length);

              if (autoDeferredTasks.length === 0) return null;

              return (
                <div className="bg-white border-2 border-stone-300 rounded-xl overflow-hidden flex flex-col" style={{ height: '400px' }}>
                  <div className="bg-stone-50 p-4 border-b-2 border-stone-300 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-stone-900">Auto-Rescheduled</h3>
                      <span className="bg-stone-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {autoDeferredTasks.length} RD tasks
                      </span>
                    </div>
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 text-xs text-teal-900">
                      <strong>Why moved:</strong> These Regular Decision tasks don't need to happen until after your Early Decision deadline (Nov 5). The system automatically rescheduled them to Nov 6-26 so you can focus on ED first.
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-3">
                      {autoDeferredTasks.slice(0, 10).map((task, idx) => {
                        const isUpdateFormat = task.taskTitle && task.changes;
                        const title = isUpdateFormat ? task.taskTitle : task.title;
                        const originalDate = isUpdateFormat ?
                          (tasks.find(t => t.id === task.taskId)?.scheduled_date || tasks.find(t => t.id === task.taskId)?.due_date || 'Earlier') :
                          (task.originalDate || 'Earlier');
                        const newDate = isUpdateFormat ? task.changes?.scheduled_date : task.newDate;

                        return (
                          <div key={idx} className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                            <div className="font-medium text-sm text-slate-900 mb-1">
                              {title}
                            </div>
                            <div className="text-xs text-stone-700">
                              Was: {originalDate} → Now: {newDate}
                            </div>
                          </div>
                        );
                      })}
                      {autoDeferredTasks.length > 10 && (
                        <div className="text-xs text-stone-500 text-center pt-2">
                          + {autoDeferredTasks.length - 10} more tasks rescheduled
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Action buttons */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={executeChanges}
              className="bg-green-600 text-white px-8 py-3 rounded-lg border-2 border-slate-900 font-medium hover:bg-green-700 transition-all active:scale-95 flex items-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Apply These Changes
            </button>
            <button
              onClick={() => setState('showing_plan')}
              className="bg-white text-slate-900 px-6 py-3 rounded-lg border-2 border-slate-900 font-medium hover:bg-stone-50 transition-all"
            >
              Back to Plan
            </button>
          </div>
        </div>
      )}

      {/* Step 6: Executing Changes */}
      {state === 'executing' && (
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <RefreshCw className="w-16 h-16 text-teal-500 animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Applying changes...</h3>
          <p className="text-stone-600 mb-4">Rescheduling {previewChanges?.updates?.length || 0} tasks</p>
          <div className="text-sm text-stone-500">This may take 10-15 seconds</div>
        </div>
      )}

      {/* Step 7: Success */}
      {state === 'success' && (
        <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-yellow-500 rounded-full mx-auto flex items-center justify-center mb-6">
            <span className="text-4xl">🎉</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">You're all set!</h1>
          <p className="text-slate-700 mb-6 leading-relaxed">
            Your timeline has been updated. You now have <strong>only {previewChanges?.summary?.tasksRescheduled || 3} tasks</strong> to worry about this week.
          </p>
          <p className="text-sm text-stone-600 mb-4">Closing in 5 seconds...</p>
          <button
            onClick={onClose}
            className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg border-2 border-slate-900 text-lg font-medium hover:bg-teal-700 transition-all active:scale-95"
          >
            Back to My Tasks
          </button>
        </div>
      )}

      {/* Error State */}
      {state === 'error' && (
        <div className="bg-white rounded-xl max-w-md w-full p-8">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h3>
            <p className="text-red-700 text-sm mb-6">{error}</p>
            <div className="flex gap-3">
              <button
                onClick={startAnalysis}
                className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700"
              >
                Try Again
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrisisSupport;